import axios, { type AxiosError, type AxiosInstance, type AxiosResponse } from 'axios';

import { authService } from '@/features/auth/services/authService';
import { AUTH_ROUTES } from '@/features/auth/routes';
import { useLoadingStore } from '@/stores/loadingStore';

import { env } from './env';
import { ApiError, type ApiErrorBody } from './apiError';

/**
 * Single Axios instance for the entire app.
 *
 * Responsibilities:
 * - Prepend base URL.
 * - Inject Cognito JWT into Authorization header.
 * - Track active requests for global loading indicator.
 * - Normalize errors into ApiError instances.
 * - Handle 401: clear session and redirect to /login.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Request interceptor: token + loading counter ────────────

apiClient.interceptors.request.use(
  async (config) => {
    useLoadingStore.getState().increment();

    try {
      const token = await authService.getAccessToken();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // No token available — let the request go without auth.
      // The backend will respond with 401 and the response interceptor handles it.
    }

    return config;
  },
  (error) => {
    useLoadingStore.getState().decrement();
    return Promise.reject(error);
  }
);

// ─── Response interceptor: error normalization + loading counter ────────────

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    useLoadingStore.getState().decrement();
    return response;
  },
  async (error: AxiosError<ApiErrorBody>) => {
    useLoadingStore.getState().decrement();

    if (!error.response) {
      return Promise.reject(
        new ApiError({
          status: 0,
          detail: 'Error de conexión. Verificá tu conexión a internet.',
          errorCode: 'network_error',
          originalError: error,
        })
      );
    }

    const status = error.response.status;
    const body = error.response.data;

    if (status === 401) {
      try {
        await authService.signOut();
      } catch {
        // ignore signOut errors
      }
      if (!window.location.pathname.startsWith(AUTH_ROUTES.LOGIN)) {
        window.location.href = AUTH_ROUTES.LOGIN;
      }
    }

    return Promise.reject(
      new ApiError({
        status,
        detail: body?.detail ?? `Error ${status}`,
        errorCode: body?.error_code ?? 'unknown_error',
        details: body?.details,
        originalError: error,
      })
    );
  }
);
